import * as React from 'react';

import {
  Formik,
} from 'formik';
import { connect } from 'react-redux';
import * as yup from 'yup';

import { IRootState, ThunkDispatch } from '@src/domain';
import { routes } from '@src/domain/Ui/UiConstants';
import { updateUser as updateUserThunk } from '@src/domain/User/UserActions';
import { Route, RouteComponentProps, RouteChildrenProps, Switch, Redirect } from 'react-router';
import { getHasSuitePaid, getHasTimelinesOrBoardsPaid, getShowGoldenTicketBanner } from '@src/domain/Organization/OrganizationSelectors';
import { UserStatuses } from '@src/domain/User/UserInterfaces';
import Modal from '../layout/Modal';
import FormField from '../legos/forms/FormField';
import GKForm from '../legos/forms/GKForm';
import { CancelButton, SubmitButton } from '../legos/forms/FormButton';
import FormStatus, { IFormStatusObject } from '../legos/forms/FormStatus';
import AvatarInput from '../legos/inputs/AvatarInput';
import NavButtonLink from '../legos/NavButtonLink';
import NavLink from '../legos/NavLink';
import PageTitle from '../legos/PageTitle';
import TrialStatusMessage from '../legos/TrialStatusMessage';
import Box from '../legos/primitives/Box';
import Flex from '../legos/primitives/Flex';
import Alert from '../legos/primitives/Alert';
import ChangePassword from './ChangePassword';
import DeleteAccount from './DeleteAccount';
import ConfirmEmailPrompt from './ConfirmEmailPrompt';
import GoldenTicketSpecialBanner from './GoldenTicketSpecialBanner';
import { MoveToGkDevBanner } from './MoveToGkDevBanner';

// necessary because we'd normally use `typeof AccountInfoDefaultValues`,
// but that isn't in scope here
export interface IAccountInfoValues {
  name: string;
  username: string;
  email: string;
}

const mapStateToProps = (state: IRootState) => ({
  user: state.User.user,
  hasTimelinesOrBoardsPaid: getHasTimelinesOrBoardsPaid(state),
  hasSuitePaid: getHasSuitePaid(state),
  showGoldenTicketBanner: state.Organization.organizationsLoaded && getShowGoldenTicketBanner(state),
});

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  updateUser: (values: IAccountInfoValues) => dispatch(updateUserThunk(values)),
});

const AccountInfoValidationSchema = yup.object().shape({
  email: yup.string().email('You must type a valid email')
    .required('Email is required'),
  name: yup.string().required('Name is required'),
  username: yup.string()
    .max(39, 'Username must be less than 40 characters')
    // copy-pasted from the gk-api
    .matches(/^[a-z\d](?:[a-z\d]|-(?=[a-z\d])){0,38}$/i,
      'Username must only contain alphanumeric characters and single hyphens, and cannot begin or end with a hyphen')
    .required('Username is required'),
});

type AccountInfoProps =
  ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> &
  RouteComponentProps;

const AccountInfo: React.FC<AccountInfoProps> = ({
  history,
  user,
  updateUser,
  showGoldenTicketBanner,
}) => {
  if (!user) {
    // TODO: return loading spinner or something
    return null;
  }
  const AccountInfoDefaultValues = {
    email: user.email,
    name: user.name,
    username: user.username,
  };

  return (
    <React.Fragment>
      <PageTitle title={routes.ACCOUNT_INFO.name} />
      <MoveToGkDevBanner />
      {showGoldenTicketBanner && <GoldenTicketSpecialBanner />}
      <ConfirmEmailPrompt />
      <TrialStatusMessage />
      <AvatarInput />
      {user.hasChangeEmailRequest &&
      <Alert
        message='We have sent you a message to confirm the email change'
        type='info'
      />}
      <Formik
        // enableReinitialize
        initialValues={AccountInfoDefaultValues}
        onSubmit={async (values, { resetForm, setStatus, setSubmitting }) => {
          try {
            const updatedUser = await updateUser(values);
            setSubmitting(false);
            resetForm();
            setStatus({
              message: `${updatedUser.hasChangeEmailRequest ?
                'We have sent you a message to confirm the email change'
                :
                'Account updated successfully'}`,
              type: 'success',
            } as IFormStatusObject);
          } catch (e) {
            setSubmitting(false);
            setStatus({
              message: e.response.data.message,
              type: 'error',
            } as IFormStatusObject);
          }
        }}
        validationSchema={AccountInfoValidationSchema}
      >
        {({ dirty }) => (
          <GKForm autoComplete='off'>
            <FormField label='Name' name='name' placeholder='Name' />
            <FormField label='Username' name='username' placeholder='Username' />
            <FormField
              label={`Email Address ${user && user.status === UserStatuses.pending ? '(unverified)' : ''}`}
              name='email'
              placeholder='Email Address'
              type='email'
            />
            <FormStatus />
            <Box>
              <React.Fragment>
                <SubmitButton>Save Changes</SubmitButton>
                {dirty && <CancelButton>cancel</CancelButton>}
              </React.Fragment>
            </Box>
            <Flex
              alignItems='baseline'
              borderColor='ui.primary.light'
              borderTop='1px solid transparent'
              justifyContent='space-between'
              mt={6}
              pt={4}
            >
              {!user.impersonator && <>
                <Box>
                  <NavButtonLink
                    mr={4}
                    size='sm'
                    to={routes.CHANGE_PASSWORD.path}
                  >
                  Change Password
                  </NavButtonLink>
                </Box>
                <NavLink to={routes.DELETE_ACCOUNT.path}>Delete My Account</NavLink>
              </>}
            </Flex>
          </GKForm>
        )}
      </Formik>
      {!user.impersonator ? <Box maxWidth={320}>
        <Route path={routes.CHANGE_PASSWORD.path}>
          {(routeProps: RouteChildrenProps) => (
            <Modal
              isOpen={Boolean(routeProps.match)}
              onRequestClose={() => history.push(routes.ACCOUNT_INFO.path)}
            >
              {(onRequestClose) =>
                <ChangePassword onRequestClose={onRequestClose} {...routeProps} />
              }
            </Modal>
          )}
        </Route>
        <Route path={routes.DELETE_ACCOUNT.path}>
          {(routeProps: RouteChildrenProps) => (
            <Modal
              isOpen={Boolean(routeProps.match)}
              maxWidth={400}
              onRequestClose={() => history.push(routes.ACCOUNT_INFO.path)}
            >
              {(onRequestClose) =>
                <DeleteAccount onRequestClose={onRequestClose} {...routeProps} />
              }
            </Modal>
          )}
        </Route>
      </Box> : <Switch>
        <Redirect from={routes.DELETE_ACCOUNT.path} to={routes.ACCOUNT_INFO.path} />
        <Redirect from={routes.CHANGE_PASSWORD.path} to={routes.ACCOUNT_INFO.path} />
      </Switch>}
    </React.Fragment>
  );
};

export default connect(
  mapStateToProps, mapDispatchToProps,
)(AccountInfo);
